import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { ArrowRight, Play, CheckCircle } from "@phosphor-icons/react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/642600d4-b2ad-4c29-8e61-9c3e489676b3/images/9da15b8c1d5b5800afc855ed1fcc4e213ff7e339cc4fe8e694adce07f9ad2b50.jpeg";

const MARKERS = [
    { top: "18%", left: "22%" },
    { top: "34%", left: "68%" },
    { top: "58%", left: "14%" },
    { top: "72%", left: "58%" },
];
const SELECTED = { top: "44%", left: "42%" };

const CHECKS = ["DOCUMENTI", "MERCATO", "RISCHI", "COSTI"];

const CALLOUTS = [
    { label: "PREZZO ASTA", value: "€118.000", pos: "top-[12%] left-[4%]" },
    { label: "VALORE STIMATO", value: "€184.000", pos: "top-[10%] right-[2%]" },
    { label: "UTILE NETTO", value: "€31.400", accent: true, pos: "bottom-[30%] left-[0%]" },
    { label: "ROI", value: "22,8%", accent: true, pos: "bottom-[36%] right-[6%]" },
];

const useScore = (active, target = 87) => {
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!active) { setN(0); return; }
        let v = 0;
        const id = setInterval(() => {
            v += 3;
            if (v >= target) { setN(target); clearInterval(id); } else setN(v);
        }, 34);
        return () => clearInterval(id);
    }, [active, target]);
    return n;
};

const Chip = ({ label, value, accent, className = "", testid }) => (
    <div data-testid={testid}
         className={`rounded-lg border border-white/[0.14] bg-black/55 backdrop-blur-xl px-3.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${className}`}>
        <div className="lp-mono text-[9px] tracking-[0.22em] text-white/45">{label}</div>
        <div className={`lp-mono text-[17px] font-bold tracking-tight ${accent ? "text-[#00E599]" : "text-white"}`}>{value}</div>
    </div>
);

const ScorePanel = ({ phase, score, testId = "hero-terminal" }) => (
    <div className="w-[248px] rounded-xl border border-white/[0.14] bg-black/60 backdrop-blur-2xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.5)]" data-testid={testId}>
        <div className="flex items-center justify-between px-4 h-8 border-b border-white/[0.08]">
            <span className="lp-mono text-[9px] tracking-[0.2em] text-white/45">RADAR · FIRENZE</span>
            <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${phase >= 3 ? "bg-[#00E599]" : "bg-[#F5B800] animate-pulse"}`} />
                <span className="lp-mono text-[9px] text-white/35">LIVE</span>
            </span>
        </div>
        <div className="p-4 relative min-h-[168px]">
            {phase === 2 && <div className="lp-scanline" />}
            <AnimatePresence mode="wait">
                {phase < 3 ? (
                    <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="lp-mono text-[11px] text-white/85 mb-3">
                            <span className="lp-ellipsis">ANALISI IN CORSO</span>
                        </div>
                        <div className="space-y-2">
                            {CHECKS.map((c, i) => (
                                <motion.div key={c} initial={{ opacity: 0, x: -6 }}
                                            animate={phase >= 2 ? { opacity: 1, x: 0 } : { opacity: 0.18, x: 0 }}
                                            transition={{ delay: phase >= 2 ? 0.5 + i * 0.75 : 0 }}
                                            className="flex items-center justify-between">
                                    <span className="lp-mono text-[10px] tracking-[0.16em] text-white/60">{c}</span>
                                    <motion.span initial={{ opacity: 0, scale: 0.6 }}
                                                 animate={phase >= 2 ? { opacity: 1, scale: 1 } : {}}
                                                 transition={{ delay: 0.6 + i * 0.75 }}>
                                        <CheckCircle size={13} weight="fill" className="text-[#00E599]" />
                                    </motion.span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div key="score" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }} className="text-center pt-1" data-testid={`${testId}-score`}>
                        <div className="lp-mono text-[9px] tracking-[0.24em] text-white/45 mb-1">INVESTMENT SCORE</div>
                        <div className="lp-mono text-[44px] leading-none font-bold text-white tracking-tight">
                            {score}<span className="text-[18px] text-white/35">/100</span>
                        </div>
                        <div className="mx-auto mt-3 h-1 w-32 rounded-full bg-white/10 overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }}
                                        className="h-full rounded-full bg-[#00E599]" />
                        </div>
                        <div className="lp-mono mt-3 inline-block text-[9px] tracking-[0.2em] text-[#00E599] border border-[#00E599]/35 bg-[#00E599]/[0.08] rounded px-2.5 py-1">
                            OPPORTUNITÀ INTERESSANTE
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        <div className="px-4 pb-2.5">
            <span className="lp-mono text-[8px] tracking-[0.14em] text-white/25">DATI DIMOSTRATIVI</span>
        </div>
    </div>
);

export const LandingHero = ({ onWatchDemo }) => {
    const { user } = useAuth();
    const [phase, setPhase] = useState(0);
    const reduced = useRef(typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const score = useScore(phase >= 3 || reduced.current);

    useEffect(() => {
        if (reduced.current) { setPhase(3); return; }
        let cancelled = false;
        const timers = [];
        const run = () => {
            if (cancelled) return;
            setPhase(0);
            timers.push(setTimeout(() => setPhase(1), 1600));
            timers.push(setTimeout(() => setPhase(2), 2500));
            timers.push(setTimeout(() => setPhase(3), 6600));
            timers.push(setTimeout(run, 15000));
        };
        run();
        return () => { cancelled = true; timers.forEach(clearTimeout); };
    }, []);

    const displayPhase = reduced.current ? 3 : phase;

    return (
        <section id="top" className="relative min-h-[100svh] flex flex-col bg-[#050505] overflow-hidden lp-grain" data-testid="landing-hero">
            {/* Cinematic background */}
            <div className="absolute inset-0">
                <img src={HERO_BG} alt="" fetchPriority="high"
                     className="w-full h-full object-cover lp-kenburns opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/72 to-[#050505]/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/45" />
                <div className="absolute inset-0 bg-black/40 lg:hidden" />
            </div>

            <div className="relative z-10 flex-1 max-w-[1280px] w-full mx-auto px-6 md:px-10 grid lg:grid-cols-[1.02fr_0.98fr] items-center gap-10 pt-28 pb-16">
                {/* Copy */}
                <div className="max-w-[560px]">
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                                className="lp-mono text-[10px] tracking-[0.28em] text-[#00E599] mb-6">
                        INTELLIGENZA IMMOBILIARE · ITALIA
                    </motion.div>
                    <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
                               className="lp-display font-extrabold text-white text-5xl sm:text-6xl lg:text-[72px] leading-[0.98] tracking-tight"
                               data-testid="hero-headline">
                        Trova l'asta giusta.<br />
                        <span className="text-white/45">Prima degli altri.</span>
                    </motion.h1>
                    <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
                              className="lp-body text-white/65 text-base sm:text-lg leading-relaxed mt-6 max-w-[480px]"
                              data-testid="hero-subheadline">
                        Asta Intelligence monitora le aste immobiliari, analizza documenti, mercato,
                        rischi e costi e trasforma ogni opportunità in una decisione d'investimento.
                    </motion.p>
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
                                className="flex flex-wrap items-center gap-4 mt-9">
                        <Link to={user ? "/dashboard" : "/register"} data-testid="hero-cta-primary"
                              className="group h-[52px] px-7 inline-flex items-center gap-2.5 bg-[#FFFFFF] text-[#0A0A0A] text-[15px] font-bold rounded-full hover:bg-[#00E599] transition-colors duration-300 active:scale-[0.97]">
                            Scopri le opportunità
                            <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <button onClick={onWatchDemo} data-testid="hero-cta-secondary"
                                className="h-[52px] px-6 inline-flex items-center gap-2.5 border border-white/25 text-white/85 text-[15px] font-semibold rounded-full hover:border-white/70 hover:text-white transition-colors active:scale-[0.97]">
                            <Play size={14} weight="fill" /> Guarda come funziona
                        </button>
                    </motion.div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.65 }}
                                className="lp-mono flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-9 text-[10px] tracking-[0.14em] text-white/55"
                                data-testid="hero-pipeline">
                        {["RADAR NAZIONALE", "ANALISI AI", "UTILE NETTO", "DECISIONE"].map((s, i) => (
                            <span key={s} className="flex items-center gap-3">
                                {i > 0 && <span className="text-[#00E599]/60">→</span>}
                                <span className={i === 3 ? "text-white/75" : ""}>{s}</span>
                            </span>
                        ))}
                    </motion.div>
                </div>

                {/* Marker scene — desktop */}
                <div className="relative hidden lg:block h-[560px]" data-testid="hero-scene">
                    {MARKERS.map((m, i) => (
                        <motion.span key={i} initial={{ opacity: 0, scale: 0 }}
                                     animate={displayPhase >= 0 ? { opacity: 1, scale: 1 } : {}}
                                     transition={{ delay: 0.4 + i * 0.28, type: "spring", stiffness: 240, damping: 18 }}
                                     className="lp-marker absolute w-2 h-2 rounded-full bg-[#00E599]"
                                     style={{ top: m.top, left: m.left }} />
                    ))}
                    {/* Selected marker */}
                    <motion.div initial={{ opacity: 0, scale: 0 }}
                                animate={displayPhase >= 1 ? { opacity: 1, scale: 1 } : {}}
                                transition={{ type: "spring", stiffness: 200, damping: 16 }}
                                className="absolute" style={{ top: SELECTED.top, left: SELECTED.left }}>
                        <span className="relative block w-3 h-3 rounded-full bg-[#FFFFFF] ring-4 ring-[#00E599]/35">
                            <motion.span animate={displayPhase >= 1 ? { scale: [1, 2.6], opacity: [0.5, 0] } : {}}
                                         transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                                         className="absolute inset-0 rounded-full border border-[#00E599]" />
                        </span>
                    </motion.div>
                    {/* Connector to terminal */}
                    <motion.div initial={{ opacity: 0 }} animate={displayPhase >= 2 ? { opacity: 1 } : {}}
                                className="absolute w-px bg-gradient-to-b from-[#00E599]/70 to-transparent"
                                style={{ top: `calc(${SELECTED.top} + 14px)`, left: `calc(${SELECTED.left} + 5px)`, height: "88px" }} />
                    {/* Terminal */}
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={displayPhase >= 2 ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.55 }}
                                className="absolute" style={{ top: `calc(${SELECTED.top} + 100px)`, left: `calc(${SELECTED.left} - 108px)` }}>
                        <ScorePanel phase={displayPhase} score={score} />
                    </motion.div>
                    {/* Data callouts */}
                    {CALLOUTS.map((c, i) => (
                        <motion.div key={c.label} initial={{ opacity: 0, y: 10 }}
                                    animate={displayPhase >= 3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                                    transition={{ delay: 0.25 + i * 0.16, duration: 0.5 }}
                                    className={`absolute ${c.pos}`}>
                            <Chip label={c.label} value={c.value} accent={c.accent} testid={`hero-chip-${i}`} />
                        </motion.div>
                    ))}
                </div>

                {/* Mobile compact panel */}
                <div className="lg:hidden flex flex-col gap-4" data-testid="hero-mobile-panel">
                    <div className="flex justify-center"><ScorePanel phase={3} score={87} testId="hero-terminal-mobile" /></div>
                    <div className="grid grid-cols-2 gap-3 max-w-[420px] mx-auto w-full">
                        {CALLOUTS.map((c) => <Chip key={c.label} label={c.label} value={c.value} accent={c.accent} />)}
                    </div>
                </div>
            </div>

            {/* Bottom fade */}
            <div className="relative z-10 pb-7 flex justify-center">
                <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2.2, repeat: Infinity }}
                            className="w-px h-10 bg-gradient-to-b from-transparent via-white/40 to-transparent" />
            </div>
        </section>
    );
};
